import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { useAuth } from '@/auth/AuthProvider';
import { useAgency, writeStoredAgency } from '@/auth/AgencyProvider';
import { callRpc, useMutation } from '@/data/rpc';
import { useBillingFlags } from '@/data/useBilling';
import { zoneCity } from '@/lib/dates';

/**
 * agency-billing F5 (design §9, T4.4): "Create my agency" on the no-agency screen. Built OFF: it renders
 * only while `billing_public_flags().self_signup_enabled` is on (a failed read counts as off). The server
 * is the gate (verified email, one self-created agency per user, the switch itself); this is the form.
 * On success the new agency becomes the stored choice and the membership read runs again, so the
 * layout opens on it with a trialing account.
 */
export const SELF_CREATE_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
] as const;

export function SelfCreateAgency() {
  const { t } = useTranslation();
  const flags = useBillingFlags();
  const { client } = useAuth();
  const agency = useAgency();
  const mutation = useMutation();
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState('');
  const [zone, setZone] = React.useState<string>('America/Chicago');

  if (!flags.selfSignup) return null;
  if (!open) {
    return (
      <div className="flex flex-col items-center gap-2 border-t border-hairline pt-4">
        <p className="max-w-prose text-base text-ink-secondary">{t('agency.selfCreate.intro')}</p>
        <Button type="button" onClick={() => setOpen(true)}>
          {t('agency.selfCreate.cta')}
        </Button>
      </div>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await mutation.run(() => callRpc<string>(client, 'agency_self_create', { p_name: name.trim(), p_time_zone: zone }));
    if (!res.error && typeof res.data === 'string') {
      writeStoredAgency(res.data);
      if ('reload' in agency) agency.reload();
    }
  };

  return (
    <form onSubmit={submit} className="flex w-full flex-col gap-4 border-t border-hairline pt-4 text-left">
      <div className="flex flex-col gap-2">
        <Label htmlFor="self-create-name">{t('agency.selfCreate.name')}</Label>
        <Input id="self-create-name" value={name} maxLength={120} required onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="self-create-zone">{t('agency.selfCreate.timeZone')}</Label>
        <NativeSelect id="self-create-zone" value={zone} aria-describedby="self-create-zone-help" onChange={(e) => setZone(e.target.value)}>
          {SELF_CREATE_ZONES.map((z) => (
            <option key={z} value={z}>
              {t('agency.selfCreate.zone', { city: zoneCity(z) })}
            </option>
          ))}
        </NativeSelect>
        <p id="self-create-zone-help" className="text-sm text-ink-secondary">
          {t('agency.selfCreate.timeZoneHelp')}
        </p>
      </div>
      {mutation.error && (
        <p role="alert" className="text-base text-danger">
          {mutation.error}
        </p>
      )}
      <div className="flex gap-3">
        <Button type="submit" disabled={mutation.busy || name.trim() === ''}>
          {mutation.busy ? t('agency.selfCreate.creating') : t('agency.selfCreate.submit')}
        </Button>
        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}
